import { CalendarTenantScopeService } from '../../services/calendar-tenant-scope.service';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AccountService } from '@frontendlib/common/services/account.service';
import { Constants } from '@frontendlib/common/types/Constants';
import { ngClassDirective } from '@frontendlib/common/directives/ngClass/ngClass.directive';
import { EventsService } from '@modules/calendar-app/services/events.service';
import {
  findMyParticipant,
  normalizeEmail,
  resolveMyRsvpStatus,
  isPendingRsvp,
  resolveRsvpAppearance,
  resolveRsvpBorderColor,
  resolveRsvpStatusPrefix,
} from '@modules/calendar-app/utils/event-rsvp.util';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import { ColorUtils } from '@modules/common/utils/color-utils';
import { FlaggedIconComponent } from '@modules/elements/components/indicator-icons/flagged-icon.component';
import { FollowedIconComponent } from '@modules/elements/components/indicator-icons/followed-icon.component';
import { SnoozedIconComponent } from '@modules/elements/components/indicator-icons/snoozed-icon.component';
import { EventContextMenuDirective } from '@modules/elements/directives/event-context-menu.directive';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import type { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { catchError, EMPTY, filter, map, switchMap, take } from 'rxjs';
import { PinnedIconComponent } from '@frontendlib/components/components/icon/pinned-icon.component';

@Component({
  standalone: true,
  selector: 'stch-event-preview-big',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DateFormatPipe,
    EventContextMenuDirective,
    PinnedIconComponent,
    FlaggedIconComponent,
    SnoozedIconComponent,
    FollowedIconComponent,
    ngClassDirective,
  ],
  styles: [
    '@layer base{:host {display: block;}}',
    ':host > *:hover { background-color: var(--hover-bg-color) !important; }',
  ],
  templateUrl: './event-preview-big.component.html',
})
export class EventPreviewBigComponent {
  public readonly inputEvent = input.required<CalendarEventSummaryDto>({ alias: 'event' });
  public readonly event = signal<CalendarEventSummaryDto>(undefined!); // writable signal to allow updates
  public readonly tabindex = input<number>();
  public readonly selected = input<boolean>();
  public readonly withContextMenu = input<boolean>(true);

  private readonly stitchEventsService = inject(StitchEventsService);
  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly calendarTenantScope = inject(CalendarTenantScopeService, { optional: true });
  private readonly signalSpaceIds = () =>
    [this.spaceContextStore.contextId(), this.calendarTenantScope?.effectiveTenantId(), this.event()?.accountId].filter(
      (id): id is string => !!id,
    );
  private readonly accountService = inject(AccountService);
  private readonly eventsService = inject(EventsService);

  private readonly currentUserEmail = toSignal(
    this.accountService.getAccount().pipe(map((account) => normalizeEmail(account.email))),
    { initialValue: null },
  );

  protected readonly eventColor = computed<string>(() => this.event().color ?? Constants.DEFAULT_EVENT_COLOR);
  public readonly isPast = computed(() => this.event().endTime < new Date());
  private readonly myRsvpStatus = computed(() => resolveMyRsvpStatus(this.event(), this.currentUserEmail()));
  public readonly bgColor = computed(() =>
    isPendingRsvp(this.myRsvpStatus()) ? 'var(--color-background)' : ColorUtils.setHexLightness(this.eventColor(), 97),
  );
  public readonly hoverColor = computed(() =>
    isPendingRsvp(this.myRsvpStatus()) ? 'var(--color-hover)' : ColorUtils.setHexLightness(this.eventColor(), 94),
  );
  private readonly rsvpAppearance = computed(() => resolveRsvpAppearance(this.myRsvpStatus()));

  protected readonly isPending = computed(() => this.rsvpAppearance() === 'pending');
  protected readonly isTentative = computed(() => this.rsvpAppearance() === 'tentative');
  protected readonly isSettled = computed(() => this.rsvpAppearance() === 'settled');

  /**
   * Every edge's colour comes from here, so `resolveRsvpBorderColor` stays the only place that
   * decides what a state looks like. The variant classes carry widths and padding only — a class
   * and this shorthand must never both claim `border-color`.
   */
  protected readonly borderColor = computed<string | null>(() => {
    switch (this.rsvpAppearance()) {
      case 'pending':
      case 'tentative':
        return this.statusColor();
      case 'settled':
        return `transparent transparent transparent ${this.statusColor() ?? 'transparent'}`;
    }
  });
  private readonly statusColor = computed(() =>
    resolveRsvpBorderColor(
      this.myRsvpStatus(),
      this.eventColor(),
      this.isPast() ? ColorUtils.setHexLightness(this.eventColor(), 85) : this.eventColor(),
    ),
  );
  protected readonly statusPrefix = computed<string | null>(() => resolveRsvpStatusPrefix(this.myRsvpStatus()));

  private readonly stitchUpdatesSignal = toSignal(
    this.stitchEventsService.getEvents(this.signalSpaceIds, StitchType.EVENT, [SignalStitchEventType.UPDATED]).pipe(
      filter((payload) => payload.ids.includes(this.event().id)),
      map((payload) => payload.changes),
    ),
  );

  /**
   * Participant RSVP status isn't carried by the lightweight UPDATED `changes`
   * payload (it deserializes to a summary DTO without participants). For events
   * the signed-in user can RSVP to, refetch the event on update so the RSVP
   * treatment stays live (e.g. answering from the dynamic panel). Gated so only
   * events the user attends refetch — and silently ignored for virtual
   * occurrences that can't be fetched by id.
   */
  private readonly refetchedEvent = toSignal(
    this.stitchEventsService.getEvents(this.signalSpaceIds, StitchType.EVENT, [SignalStitchEventType.UPDATED]).pipe(
      filter((payload) => {
        const ev = this.event();
        const parentUid = ev.recurrenceParentId ?? ev.recurrentSeriesId ?? null;
        return payload.ids.includes(ev.id) || (parentUid !== null && payload.ids.includes(parentUid));
      }),
      filter(() => !!findMyParticipant(this.event(), this.currentUserEmail())),
      // `take(1)`: getItem is getRefresh()-backed, so without it this card would keep a live
      // subscription and refetch on every forceRefresh() anywhere in the app.
      switchMap(() =>
        this.eventsService.getItem(this.event()).pipe(
          take(1),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  constructor() {
    effect(() => {
      this.event.set(this.inputEvent());
    });

    effect(() => {
      const changes = this.stitchUpdatesSignal();
      if (changes) {
        this.event.update((prev) => ({ ...prev, ...changes }));
      }
    });

    effect(() => {
      const fresh = this.refetchedEvent();
      // Keyed by id: `track $index` recycles these instances, so a late response for the previous
      // event would otherwise repaint the one now bound here.
      if (fresh && fresh.id === untracked(this.event).id) {
        this.event.set(fresh);
      }
    });
  }
}
